import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, FunnelChart, Funnel, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { ArrowLeft, TrendingUp, MousePointerClick, Eye, Lock, BarChart3, FlaskConical } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, subDays, startOfDay, eachDayOfInterval } from "date-fns";

type ConversionRow = {
  event_name: string;
  section_id: string | null;
  session_id: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
};

const FUNNEL_STEPS = [
  { key: "peek_preview_started", label: "Preview Started", color: "hsl(var(--primary))" },
  { key: "peek_locked", label: "Content Locked", color: "hsl(var(--primary) / 0.7)" },
  { key: "peek_unlock_clicked", label: "Unlock Clicked", color: "hsl(var(--primary) / 0.5)" },
];

const ALL_EVENTS = [
  "peek_preview_started", "peek_locked", "peek_unlock_clicked",
  "exit_intent_shown", "exit_intent_clicked",
  "flash_offer_shown", "flash_offer_clicked",
  "sticky_bar_clicked", "fomo_toast_shown",
  "ab_test_impression",
];

const EVENT_LABELS: Record<string, string> = {
  peek_preview_started: "Preview Started",
  peek_locked: "Content Locked",
  peek_unlock_clicked: "Unlock Clicked",
  exit_intent_shown: "Exit Intent Shown",
  exit_intent_clicked: "Exit Intent Clicked",
  flash_offer_shown: "Flash Offer Shown",
  flash_offer_clicked: "Flash Offer Clicked",
  sticky_bar_clicked: "Sticky Bar Clicked",
  fomo_toast_shown: "FOMO Toast Shown",
  ab_test_impression: "A/B Test Impression",
};

const AdminAnalytics = () => {
  const { user, loading } = useAuth();
  const [days, setDays] = useState("7");

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["conversion-analytics", days],
    queryFn: async () => {
      const since = subDays(new Date(), parseInt(days)).toISOString();
      const { data, error } = await (supabase.from("conversion_events") as any)
        .select("event_name, section_id, session_id, created_at, metadata")
        .gte("created_at", since)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as ConversionRow[];
    },
    refetchInterval: 30000,
  });

  // Funnel data
  const funnelData = useMemo(() => {
    return FUNNEL_STEPS.map((step) => ({
      name: step.label,
      value: events.filter((e) => e.event_name === step.key).length,
      fill: step.color,
    }));
  }, [events]);

  // Conversion rates by section
  const sectionData = useMemo(() => {
    const sections = [...new Set(events.filter((e) => e.section_id).map((e) => e.section_id!))];
    return sections.map((s) => {
      const started = events.filter((e) => e.event_name === "peek_preview_started" && e.section_id === s).length;
      const clicked = events.filter((e) => e.event_name === "peek_unlock_clicked" && e.section_id === s).length;
      const rate = started > 0 ? Math.round((clicked / started) * 100) : 0;
      return { section: s, started, clicked, rate };
    });
  }, [events]);

  // Trend over time
  const trendData = useMemo(() => {
    const d = parseInt(days);
    const interval = eachDayOfInterval({ start: subDays(new Date(), d - 1), end: new Date() });
    return interval.map((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayStart = startOfDay(day).getTime();
      const dayEnd = dayStart + 86400000;
      const dayEvents = events.filter((e) => {
        const t = new Date(e.created_at).getTime();
        return t >= dayStart && t < dayEnd;
      });
      return {
        date: format(day, "MMM dd"),
        previews: dayEvents.filter((e) => e.event_name === "peek_preview_started").length,
        clicks: dayEvents.filter((e) => e.event_name === "peek_unlock_clicked").length,
        exits: dayEvents.filter((e) => e.event_name === "exit_intent_clicked").length,
        flash: dayEvents.filter((e) => e.event_name === "flash_offer_clicked").length,
        sticky: dayEvents.filter((e) => e.event_name === "sticky_bar_clicked").length,
      };
    });
  }, [events, days]);

  // Summary stats
  const stats = useMemo(() => {
    const total = events.length;
    const sessions = new Set(events.map((e) => e.session_id)).size;
    const unlocks = events.filter((e) => e.event_name === "peek_unlock_clicked").length;
    const previews = events.filter((e) => e.event_name === "peek_preview_started").length;
    const convRate = previews > 0 ? ((unlocks / previews) * 100).toFixed(1) : "0";
    const exitShown = events.filter((e) => e.event_name === "exit_intent_shown").length;
    const exitClicked = events.filter((e) => e.event_name === "exit_intent_clicked").length;
    const flashShown = events.filter((e) => e.event_name === "flash_offer_shown").length;
    const flashClicked = events.filter((e) => e.event_name === "flash_offer_clicked").length;
    const exitRate = exitShown > 0 ? ((exitClicked / exitShown) * 100).toFixed(1) : "0";
    const flashRate = flashShown > 0 ? ((flashClicked / flashShown) * 100).toFixed(1) : "0";
    return { total, sessions, unlocks, convRate, exitShown, exitClicked, exitRate, flashShown, flashClicked, flashRate };
  }, [events]);

  // Hero headline A/B test breakdown
  const heroABData = useMemo(() => {
    const impressions = events.filter((e) => e.event_name === "ab_test_impression" && e.section_id === "hero_headline");
    const variants: Record<string, { impressions: number; sessions: Set<string> }> = {};
    impressions.forEach((e) => {
      const v = (e.metadata as any)?.variant as string || "unknown";
      if (!variants[v]) variants[v] = { impressions: 0, sessions: new Set() };
      variants[v].impressions++;
      if (e.session_id) variants[v].sessions.add(e.session_id);
    });
    // Count conversions (unlock clicks) per session to attribute to variant
    const sessionVariant: Record<string, string> = {};
    impressions.forEach((e) => {
      if (e.session_id) sessionVariant[e.session_id] = (e.metadata as any)?.variant || "unknown";
    });
    const conversionEvents = events.filter((e) => 
      e.event_name === "peek_unlock_clicked" || e.event_name === "exit_intent_clicked" || 
      e.event_name === "flash_offer_clicked" || e.event_name === "sticky_bar_clicked"
    );
    const variantConversions: Record<string, number> = {};
    conversionEvents.forEach((e) => {
      if (e.session_id && sessionVariant[e.session_id]) {
        const v = sessionVariant[e.session_id];
        variantConversions[v] = (variantConversions[v] || 0) + 1;
      }
    });
    return Object.entries(variants).map(([variant, data]) => ({
      variant,
      impressions: data.impressions,
      uniqueSessions: data.sessions.size,
      conversions: variantConversions[variant] || 0,
      rate: data.sessions.size > 0 ? ((variantConversions[variant] || 0) / data.sessions.size * 100).toFixed(1) : "0",
    }));
  }, [events]);

  // Flash offer A/B test breakdown
  const flashABData = useMemo(() => {
    const shown = events.filter((e) => e.event_name === "flash_offer_shown");
    const clicked = events.filter((e) => e.event_name === "flash_offer_clicked");
    const variants: Record<string, { shown: number; clicked: number; price: number }> = {};
    shown.forEach((e) => {
      const v = (e.metadata as any)?.variant as string || "?";
      const p = (e.metadata as any)?.price as number || 0;
      if (!variants[v]) variants[v] = { shown: 0, clicked: 0, price: p };
      variants[v].shown++;
    });
    clicked.forEach((e) => {
      const v = (e.metadata as any)?.variant as string || "?";
      if (variants[v]) variants[v].clicked++;
    });
    return Object.entries(variants).map(([variant, d]) => ({
      variant,
      price: `€${d.price.toFixed(2)}`,
      shown: d.shown,
      clicked: d.clicked,
      rate: d.shown > 0 ? ((d.clicked / d.shown) * 100).toFixed(1) : "0",
    }));
  }, [events]);

  // Event breakdown
  const eventBreakdown = useMemo(() => {
    return ALL_EVENTS.map((e) => ({
      name: EVENT_LABELS[e] || e,
      count: events.filter((ev) => ev.event_name === e).length,
    })).filter((e) => e.count > 0);
  }, [events]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto flex items-center gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex-1">
            <h1 className="text-lg font-black text-foreground">Conversion Analytics</h1>
            <p className="text-xs text-muted-foreground">Track premium conversion funnel performance</p>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24h</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Eye className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Total Events</span>
              </div>
              <p className="text-2xl font-black text-foreground">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <BarChart3 className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Sessions</span>
              </div>
              <p className="text-2xl font-black text-foreground">{stats.sessions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MousePointerClick className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Unlock Clicks</span>
              </div>
              <p className="text-2xl font-black text-primary">{stats.unlocks}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Conv. Rate</span>
              </div>
              <p className="text-2xl font-black text-primary">{stats.convRate}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Flash vs Exit Intent comparison */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Eye className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Exit Intent Shown</span>
              </div>
              <p className="text-2xl font-black text-foreground">{stats.exitShown}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MousePointerClick className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Exit Conv. Rate</span>
              </div>
              <p className="text-2xl font-black text-primary">{stats.exitRate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Eye className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Flash Offer Shown</span>
              </div>
              <p className="text-2xl font-black text-foreground">{stats.flashShown}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Flash Conv. Rate</span>
              </div>
              <p className="text-2xl font-black text-primary">{stats.flashRate}%</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Funnel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                Peek → Lock → Click Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">Loading...</div>
              ) : funnelData.every((d) => d.value === 0) ? (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                      {funnelData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {funnelData[0]?.value > 0 && (
                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                  <span>Lock rate: <strong className="text-foreground">{((funnelData[1].value / funnelData[0].value) * 100).toFixed(0)}%</strong></span>
                  <span>Click rate: <strong className="text-primary">{((funnelData[2].value / funnelData[0].value) * 100).toFixed(0)}%</strong></span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold">Conversion by Section</CardTitle>
            </CardHeader>
            <CardContent>
              {sectionData.length === 0 ? (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={sectionData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="section" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={((value: number, name: string) => [name === "rate" ? `${value}%` : value, name]) as any}
                    />
                    <Bar dataKey="started" fill="hsl(var(--primary) / 0.3)" radius={[4, 4, 0, 0]} name="Previews" />
                    <Bar dataKey="clicked" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {sectionData.length > 0 && (
                <div className="mt-2 space-y-1">
                  {sectionData.map((s) => (
                    <div key={s.section} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground capitalize">{s.section}</span>
                      <span className="font-bold text-primary">{s.rate}% conv.</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Trend over time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold">Trend Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData} margin={{ left: 0, right: 10, top: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="previews" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Previews" />
                  <Line type="monotone" dataKey="clicks" stroke="hsl(var(--primary) / 0.6)" strokeWidth={2} dot={false} name="Unlock Clicks" />
                   <Line type="monotone" dataKey="exits" stroke="hsl(var(--destructive))" strokeWidth={1.5} dot={false} name="Exit Intent" />
                   <Line type="monotone" dataKey="flash" stroke="hsl(var(--warning, 38 92% 50%))" strokeWidth={1.5} dot={false} name="Flash Offer" />
                   <Line type="monotone" dataKey="sticky" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} dot={false} name="Sticky Bar" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* All events breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold">All Events Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {eventBreakdown.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={eventBreakdown} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Hero Headline A/B Test */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-primary" />
              Hero Headline A/B Test
            </CardTitle>
          </CardHeader>
          <CardContent>
            {heroABData.length === 0 ? (
              <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">No A/B test data yet</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-2 text-xs font-medium text-muted-foreground border-b border-border pb-2">
                  <span>Variant</span>
                  <span className="text-center">Impressions</span>
                  <span className="text-center">Sessions</span>
                  <span className="text-center">Conversions</span>
                  <span className="text-right">Conv. Rate</span>
                </div>
                {heroABData.map((row) => (
                  <div key={row.variant} className="grid grid-cols-5 gap-2 text-sm items-center">
                    <span className="font-semibold text-foreground capitalize">{row.variant}</span>
                    <span className="text-center text-muted-foreground">{row.impressions}</span>
                    <span className="text-center text-muted-foreground">{row.uniqueSessions}</span>
                    <span className="text-center text-foreground font-medium">{row.conversions}</span>
                    <span className="text-right font-bold text-primary">{row.rate}%</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Flash Offer A/B Test (€1.99 vs €0.99) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-destructive" />
              Flash Offer Price A/B Test
            </CardTitle>
          </CardHeader>
          <CardContent>
            {flashABData.length === 0 ? (
              <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">No flash A/B data yet</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-2 text-xs font-medium text-muted-foreground border-b border-border pb-2">
                  <span>Variant</span>
                  <span className="text-center">Price</span>
                  <span className="text-center">Shown</span>
                  <span className="text-center">Clicked</span>
                  <span className="text-right">Conv. Rate</span>
                </div>
                {flashABData.map((row) => (
                  <div key={row.variant} className="grid grid-cols-5 gap-2 text-sm items-center">
                    <span className="font-semibold text-foreground">Variant {row.variant}</span>
                    <span className="text-center text-muted-foreground">{row.price}</span>
                    <span className="text-center text-muted-foreground">{row.shown}</span>
                    <span className="text-center text-foreground font-medium">{row.clicked}</span>
                    <span className="text-right font-bold text-primary">{row.rate}%</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
